import { useCallback, useEffect, useState } from 'react';
import { GetPromotionContents } from 'src/graphql/queries/hygraph/PromotionContentQuery';
import { isTransientError } from '@brighte/passwordless-authenticator';
import { ExistingApplicationModalProps } from '@components/ExistingApplicationModal';
import { datadogRum } from '@datadog/browser-rum';
import {
  CalculateRebatesQueryVariables,
  CuratedArticlesQuery,
  CuratedArticlesResponse,
  FaqsQuery,
  FaqsResponse,
  GetPackageForOpportunityQuery,
  GetPackageForOpportunityQueryVariables,
  PackageQuote,
  PromotionContentsResponse,
  ProposalDownloadLinkQuery,
  ProposalDownloadLinkQueryVariables,
  QuotePrepopValuesByTrackingIdQuery,
  QuotePrepopValuesByTrackingIdQueryVariables,
  QuoteRequestsQueryVariables,
  ReferralOpportunity,
  ReferralQueryVariables,
  SelectablePackageQuote,
  SelectQuoteForOpportunityResponse,
  VendorsAwaitingQuotesQueryVariables,
  hygraphRequest,
  useCalculateRebatesQuery,
  useGetPackageForOpportunityQuery,
  useProposalDownloadLinkQuery,
  useQuotePrepopValuesByTrackingIdQuery,
  useQuoteRequestsQuery,
  useReferralEnterpriseLeadApplicationsQuery,
  useReferralQuery,
  useSelectQuoteForOpportunityMutation,
  useVendorsAwaitingQuotesQuery,
} from '@graphql';
import { usePackageContext } from '@provider';
import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { FINANCIAL_PRODUCT, PACKAGE_CATEGORY_SELECTION_MODE } from '@utils/constants';
import { accessibleQuote } from '@utils/helper';
import {
  ExistingApplicationBlockReason,
  getExistingApplicationBlockReason,
  transformSelectQuoteForOpportunityInput,
} from './helper';

export { usePackagesByOpportunity } from './usePackagesByOpportunity';
export type { OpportunityPackageStatus } from './usePackagesByOpportunity';

export interface PackageData {
  requiredCategories: { name: string; slug: string }[];
  quotesByCategory: {
    name: string;
    slug: string;
    quotes: PackageQuote[];
  }[];
  preselectedQuotes: PackageQuote[];
  preselectedVendor: string;
  isPackageComplete: boolean;
  opportunity: ReferralOpportunity;
  flattenedQuotes: PackageQuote[];
}

export function useDatadogAddError() {
  const { packageForm } = usePackageContext();

  const logError = useCallback(
    (error: Error) => {
      const errorMessage = typeof error === 'string' ? error : error?.message;
      if (errorMessage) {
        const logMessage = `${packageForm.financialProduct?.id ?? ''} error: ${errorMessage}`;
        datadogRum.addError(new Error(logMessage));
      }
    },
    [packageForm.financialProduct?.id],
  );

  return {
    logError,
  };
}

export function useCalculateRebates(
  variables: CalculateRebatesQueryVariables,
  options?: Pick<UseQueryOptions, 'enabled'>,
) {
  const { isLoading, data, error } = useCalculateRebatesQuery(variables, {
    ...options,
    select: data => data.calculateRebates,
  });

  return {
    data,
    loading: isLoading,
    error,
  };
}

type PrepopOptions = Pick<
  UseQueryOptions<
    QuotePrepopValuesByTrackingIdQuery,
    unknown,
    QuotePrepopValuesByTrackingIdQuery['quotePrepopValuesByTrackingId']
  >,
  'refetchInterval'
>;

export function usePrepop(variables: QuotePrepopValuesByTrackingIdQueryVariables, options?: PrepopOptions) {
  const { isLoading, data, error } = useQuotePrepopValuesByTrackingIdQuery(variables, {
    select: data => data.quotePrepopValuesByTrackingId,
    enabled: variables.trackingId.trim() !== '',
    refetchInterval: options?.refetchInterval,
  });

  return {
    data,
    loading: isLoading,
    error,
  };
}

export function useReferral(variables: ReferralQueryVariables) {
  const { setPackageForm } = usePackageContext();

  // Partner config is sourced from prepop rather than the referral query: prepop is keyed by
  // the same trackingId and already fetched at the root Pages route, so this resolves from the
  // React Query cache without an extra backend call.
  const { data: prepopData } = usePrepop({ trackingId: variables.id });
  const prepopPartnerConfig = prepopData?.enterprisePartnerConfig;

  const [packageData, setPackageData] = useState<PackageData>({
    requiredCategories: [],
    quotesByCategory: [],
    preselectedQuotes: [],
    preselectedVendor: '',
    isPackageComplete: false,
    opportunity: {} as ReferralOpportunity,
    flattenedQuotes: [],
  });

  const {
    data: quotesData,
    isLoading,
    error,
  } = useReferralQuery(variables, { enabled: variables.id.trim() !== '', useErrorBoundary: true });

  useEffect(() => {
    setPackageForm({
      enterprisePartnerConfig: prepopPartnerConfig
        ? { title: prepopPartnerConfig.title, cmsContentSlug: prepopPartnerConfig.cmsContentSlug }
        : null,
    });
  }, [prepopPartnerConfig, setPackageForm]);

  useEffect(() => {
    if (quotesData) {
      const { referral: mainQuote } = quotesData;
      const { opportunity, financialProduct, productConfiguration, address, relatedReferrals } = mainQuote;

      if (!opportunity?.id || !relatedReferrals) {
        return;
      }

      // Normalise related referrals to the mainQuote shape (copy financialProduct, ensure an
      // opportunity object).
      const updatedRelatedQuotes = relatedReferrals.map(quote => {
        const existingOpportunity = (quote as { opportunity?: PackageQuote['opportunity'] }).opportunity;

        return {
          ...quote,
          financialProduct,
          productConfiguration,
          // Only derive opportunity from the flat opportunityId when one isn't already present.
          ...(existingOpportunity ? {} : { opportunity: quote.opportunityId ? { id: quote.opportunityId } : null }),
        };
      });

      const flattenedQuotes = [mainQuote, ...updatedRelatedQuotes].filter(quote =>
        accessibleQuote(quote.status, financialProduct.id),
      );

      const requiredCategories = [...new Set(opportunity.categories?.map(category => category))];

      const preselectedQuoteIds = mainQuote?.selectedPackage?.trackingIds ?? [];
      const preselectedQuotes = flattenedQuotes.filter(quote =>
        preselectedQuoteIds.includes(quote.id ?? ''),
      ) as PackageQuote[];

      const joinedCategories = [...flattenedQuotes.map(quote => quote.category), ...requiredCategories.map(cat => cat)];
      const categoryList = joinedCategories.filter(
        (category, index, self) => index === self.findIndex(cat => cat.slug === category.slug),
      );

      const quotesByCategory = categoryList.map(category => ({
        ...category,
        quotes: flattenedQuotes.filter(quote => quote.category?.name === category.name) as PackageQuote[],
      }));

      setPackageForm({
        opportunityId: opportunity.id,
        financialProduct,
        productConfiguration,
        address,
        enterprisePartnerId: mainQuote.enterprisePartnerId ?? '',
      });

      setPackageData({
        requiredCategories,
        quotesByCategory,
        preselectedQuotes,
        preselectedVendor: '',
        isPackageComplete:
          PACKAGE_CATEGORY_SELECTION_MODE[financialProduct.id] === 'all'
            ? requiredCategories.every(category => categoryList.find(cat => cat.slug === category.slug))
            : true,
        opportunity,
        flattenedQuotes: flattenedQuotes as PackageQuote[],
      });
    }
  }, [quotesData, setPackageForm]);

  return {
    data: packageData,
    loading: isLoading,
    error,
  };
}

export function useVendorsAwaitingQuotes(variables: VendorsAwaitingQuotesQueryVariables) {
  const { isLoading, data, error } = useVendorsAwaitingQuotesQuery(variables, {
    select: data => data.referral.opportunity?.vendorsAwaitingQuotes,
  });

  return {
    data,
    loading: isLoading,
    error,
  };
}

// CommBank-only: looks up the referral's existing applications and decides whether a new package
// selection must be blocked. Other partners run no query and never block.
function useCommBankExistingApplicationBlock({
  selectedQuotes,
  trackingId,
  acceptedPackage,
}: {
  selectedQuotes: PackageQuote[];
  trackingId?: string;
  acceptedPackage: GetPackageForOpportunityQuery['getPackageForOpportunity'];
}): {
  requiresBlock: boolean;
  blockReason: ExistingApplicationBlockReason | null;
  isSettled: boolean;
} {
  const isCommBank = selectedQuotes?.[0]?.financialProduct?.id === FINANCIAL_PRODUCT.CBA_RESIDENTIAL;
  const referralId = trackingId?.trim() ?? '';
  const enabled = isCommBank && referralId !== '';

  // Existing applications for the referral's lead (CommBank-only, hence `enabled`).
  const applicationsQuery = useReferralEnterpriseLeadApplicationsQuery(
    { id: referralId },
    { enabled, select: data => data.referral.enterpriseLead?.applications ?? [] },
  );

  useEffect(() => {
    if (applicationsQuery.error) {
      // Fail open: quote acceptance must not be blocked when the existing application lookup fails.
      const message =
        applicationsQuery.error instanceof Error ? applicationsQuery.error.message : String(applicationsQuery.error);
      datadogRum.addError(
        new Error(`Failed to fetch existing lead applications for referral "${referralId}": ${message}`),
      );
    }
  }, [applicationsQuery.error, referralId]);

  // CBA is submit-once (no resume flow): an active application blocks; DECLINED/WITHDRAWN don't
  // (blockReason null). Matched to the active package by packageId === enterprise orderId.
  const blockReason = getExistingApplicationBlockReason(applicationsQuery.data, acceptedPackage?.packageId);

  const isSettled = !enabled || applicationsQuery.isSuccess || applicationsQuery.isError;

  return {
    requiresBlock: blockReason !== null,
    blockReason,
    isSettled,
  };
}

export function useSelectQuoteForOpportunity({
  selectedQuotes,
  isCashPayment = false,
  trackingId,
  handlePackageSubmission,
  onRegenerateConfirmationRequired,
  skipExistingApplicationCheck = false,
}: {
  selectedQuotes: SelectablePackageQuote[];
  isCashPayment?: boolean;
  // Referral trackingId used to look up existing applications.
  trackingId?: string;
  handlePackageSubmission: (data: SelectQuoteForOpportunityResponse) => void;
  // INT-1219: the customer's current CommBank application is no longer usable; a new one
  // is only created after they confirm the regenerate dialog.
  onRegenerateConfirmationRequired?: () => void;
  // The existing-application warning only makes sense when submitting could supersede a
  // DIFFERENT in-flight application (a new package selection). A caller that's resuming the
  // same already-accepted quote's application (INT-1320's "Continue with application") has
  // nothing to supersede, so it opts out entirely rather than silently waiting on these two
  // lookups to settle before the status-check mutation can fire on click.
  skipExistingApplicationCheck?: boolean;
}) {
  const [isExistingApplicationModalOpen, setIsExistingApplicationModalOpen] = useState(false);
  const [isSubmitPending, setIsSubmitPending] = useState(false);

  const opportunityId = selectedQuotes?.[0]?.opportunity?.id ?? '';

  // The package currently accepted for this opportunity: re-accepting the exact same package is a
  // resume of the existing application rather than a supersede, so it must not trigger the warning.
  const acceptedPackageEnabled = !skipExistingApplicationCheck && opportunityId !== '';
  const acceptedPackageQuery = useGetPackageForOpportunityQuery(
    { opportunityId },
    { enabled: acceptedPackageEnabled, select: data => data.getPackageForOpportunity },
  );
  const acceptedPackage = acceptedPackageQuery.data;

  // CommBank-only: decides whether this submission must be blocked by an existing application.
  const {
    requiresBlock: requiresExistingApplicationWarning,
    blockReason,
    isSettled: existingApplicationSettled,
  } = useCommBankExistingApplicationBlock({
    selectedQuotes: skipExistingApplicationCheck ? [] : selectedQuotes,
    trackingId: skipExistingApplicationCheck ? undefined : trackingId,
    acceptedPackage,
  });

  // All lookups have settled (or never ran): only then can the existing-application gate
  // decide. Submissions made earlier are held in isSubmitPending and released below.
  const lookupsSettled =
    existingApplicationSettled &&
    (!acceptedPackageEnabled || acceptedPackageQuery.isSuccess || acceptedPackageQuery.isError);

  const {
    mutate: selectQuoteForOpportunity,
    isLoading,
    isSuccess,
  } = useSelectQuoteForOpportunityMutation({
    useErrorBoundary: true,
    // A brief backend blip (e.g. a 502 during a deploy) shouldn't strand the customer on the
    // generic error page for something that would have succeeded a moment later — retry
    // transient failures a couple of times before giving up and falling through to the boundary.
    retry: (failureCount, error) => isTransientError(error) && failureCount < 2,
    retryDelay: retryCount => 1000 * 2 ** retryCount,
    onSuccess: ({ selectQuoteForOpportunity }) => {
      if (selectQuoteForOpportunity.requiresRegenerateConfirmation) {
        // A caller on a CBA-reachable page must handle this to show the regenerate dialog.
        // Without a handler the response has no applicationUrl and nothing would happen —
        // a silent stuck state (INT-1219). A throw here would be an unhandled error
        // (useErrorBoundary does not intercept lifecycle callbacks), so report to Datadog
        // and return instead.
        if (!onRegenerateConfirmationRequired) {
          datadogRum.addError(new Error('Regenerate confirmation required but no handler was provided'));
          return;
        }
        onRegenerateConfirmationRequired();
        return;
      }

      datadogRum.addAction('Submitted Customer Package', {
        category_count: selectedQuotes.length,
        cash_payment: isCashPayment,
      });
      datadogRum.addAction('Submitted Customer Package Category', {
        quote_ids: selectedQuotes.map(quote => quote.id),
      });

      if (selectQuoteForOpportunity.packageId) {
        handlePackageSubmission(selectQuoteForOpportunity);
      }
    },
  });

  const submitQuotes = useCallback(
    (confirmRegenerate: boolean) => {
      const applicationInput = transformSelectQuoteForOpportunityInput({
        selectedQuotes,
        isCashPayment,
        confirmRegenerate,
      });

      if (applicationInput) {
        selectQuoteForOpportunity(applicationInput);
      }
    },
    [selectedQuotes, isCashPayment, selectQuoteForOpportunity],
  );

  // Every submission defers through this pending flag so there is a single gated path:
  // submitting while the lookups are still in flight would otherwise silently bypass the
  // existing-application modal. Once the lookups settle the effect below releases it.
  const selectQuotes = () => {
    setIsSubmitPending(true);
  };

  useEffect(() => {
    if (!isSubmitPending || !lookupsSettled) {
      return;
    }

    setIsSubmitPending(false);

    if (requiresExistingApplicationWarning) {
      datadogRum.addAction('Existing Application Blocked');
      setIsExistingApplicationModalOpen(true);
      return;
    }

    submitQuotes(false);
  }, [isSubmitPending, lookupsSettled, requiresExistingApplicationWarning, submitQuotes]);

  // INT-1219: re-submit after the customer confirms the regenerate dialog. The
  // existing-application gate has already run on the initial submission.
  const confirmRegenerateQuotes = () => submitQuotes(true);

  const existingApplicationModal: ExistingApplicationModalProps | undefined =
    requiresExistingApplicationWarning && blockReason
      ? {
          isOpen: isExistingApplicationModalOpen,
          onToggle: () => setIsExistingApplicationModalOpen(false),
          variant: blockReason,
        }
      : undefined;

  return {
    selectQuotes,
    confirmRegenerateQuotes,
    isLoading: isLoading || isSubmitPending,
    isSuccess,
    existingApplicationModal,
  };
}

export function useProposalDownloadLink(
  variables: ProposalDownloadLinkQueryVariables,
  options?: UseQueryOptions<ProposalDownloadLinkQuery, string, string>,
) {
  return useProposalDownloadLinkQuery(variables, {
    enabled: !!variables.proposalId,
    select: data => data.proposalDownloadLink,
    ...options,
  });
}

export function useGetPackageForOpportunity(
  variables: GetPackageForOpportunityQueryVariables,
  options?: Pick<UseQueryOptions, 'enabled'>,
) {
  const { isLoading, data, error } = useGetPackageForOpportunityQuery(variables, {
    select: data => data.getPackageForOpportunity,
    ...options,
  });

  return {
    isLoading,
    data,
    error,
  };
}

export function useCuratedArticlesQuery(slug: string) {
  const { data, isLoading } = useQuery<CuratedArticlesResponse, unknown, CuratedArticlesResponse['values']>({
    queryKey: ['curated-articles', slug],
    queryFn: async () => {
      const client = hygraphRequest();
      const filter = {
        where: {
          slug: `${slug}-articles`,
        },
        first: 5,
      };
      return client.request(CuratedArticlesQuery, filter);
    },
    select: data => data?.values,
    enabled: !!slug,
  });

  return {
    data,
    isLoading,
  };
}

export function useFaqsQuery(slug: string) {
  const { data, isLoading } = useQuery<FaqsResponse, unknown, FaqsResponse['values']>({
    queryKey: ['faqs', slug],
    queryFn: async () => {
      const client = hygraphRequest();
      const filter = {
        where: {
          slug,
        },
        first: 5,
      };
      return client.request(FaqsQuery, filter);
    },
    select: data => data?.values,
    enabled: !!slug,
  });

  return {
    data,
    isLoading,
  };
}

export function usePromotionContent({ promoCodes, partnerId }: { promoCodes: string[]; partnerId: string }) {
  const { data, isLoading } = useQuery<PromotionContentsResponse, unknown, PromotionContentsResponse['promotions']>({
    queryKey: ['promotion-content', partnerId, promoCodes],
    queryFn: async () => {
      const client = hygraphRequest();
      const filter = {
        partner: partnerId,
        promotionCodes: promoCodes,
      };
      return client.request(GetPromotionContents, filter);
    },
    select: data => data?.promotions,
  });

  return {
    data,
    isLoading,
  };
}

export const useQuoteRequests = (variables?: QuoteRequestsQueryVariables) => {
  const { isLoading, data, error } = useQuoteRequestsQuery(variables, {
    select: data => data.quoteRequests,
  });

  return {
    isLoading,
    data,
    error,
  };
};
